import { Form } from "antd"
import {
  Card,
  Divider,
  Flex,
  Legend,
  Modal,
  Number,
  SelectInput,
  Skeleton,
  Text,
  Tooltip,
  notification,
} from "components/common"
import {
  useAddSalesMutation,
  useFilerStatusMutation,
  useLazyGetFractionalProjectDataQuery,
  useLazyGetFractionalProjectsQuery,
  useLazyGetPaymentMethodQuery,
} from "features/leads/api"
import { paymentsListFullyComplete } from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/utils/paymentUtils"
import FractionalPaymentSection from "./fractionalPaymentSection"
import PsidSuccessModal from "./psidSuccessModal"
import {
  FRACTIONAL_PAYMENT_CHANNEL_PSID,
  FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
  isFractionalPsidChannelRowComplete,
  isPsidModeKey,
  resolvePsidPaymentModeKey,
} from "./fractionalPaymentUtils"
import { useFractionalSalePaymentApis } from "./useFractionalSalePaymentApis"
import { useCallback, useEffect, useMemo, useRef } from "react"
import styles from "./fractionalSaleModal.module.less"
import { FractionalSaleModalSkeleton, modalTitle, renderBlocksHeader } from "./utilis"
import { t } from "i18next"
import BlockCountStepper from "./blockCountStepper"
import { TitleDescription } from "components/custom"
import theme from "utils/themeVars"
import cx from "clsx"
import { ClientForm } from "features/clients/components"
import { useLazyGetClientDetailQuery } from "features/clients/detail/api"
import { useLazyGetProjectDetailFormQuery } from "features/projects/api"
import { debounce } from "lodash"

function isPaymentProofVisionOnProject(value) {
  if (value == null || value === "") return false
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  const s = String(value).toLowerCase()
  return s === "1" || s === "true" || s === "on" || s === "yes"
}

function formatAmount(value) {
  if (value == null || value === "") return "-"
  return <Number value={value} isCurrency tooltip className="bold" compact="" />
}

const requiredFields = [
  "dateOfBirth",
  "relationship",
  "nationalId",
  "countryId",
  "cityId",
  "localityId",
  "email",
  "name",
]

export default function FractionalSaleModal({ lead, formRef, client, from = {} }) {
  const [form] = Form.useForm()
  const clientFormRef = useRef()
  const psidSuccessModalRef = useRef()
  const [addSales, { isLoading: isAddingSale }] = useAddSalesMutation()
  const paymentApis = useFractionalSalePaymentApis()
  const [getFractionalProjects, { data: projectOptions = [], isFetching: loadingProjects, error: projectsError }] =
    useLazyGetFractionalProjectsQuery()
  const [fetchBlockData, { data: blockData, isFetching: loadingBlockData, error: blockDataError }] =
    useLazyGetFractionalProjectDataQuery()
  const [getPaymentMethod, { data: modeOfPayments = [], isFetching: loadingPaymentMethods }] =
    useLazyGetPaymentMethodQuery()
  const [fetchProjectDetail, { data: projectDetail, isFetching: loadingProjectDetail }] =
    useLazyGetProjectDetailFormQuery()
  const [getClientDetail, { data: clientData, error, isFetching }] = useLazyGetClientDetailQuery()
  const [getFilerStatus, { data: filerStatus, isFetching: loadingFilerStatus }] = useFilerStatusMutation()

  const clientId = client?.id || lead?.Client?.id

  const projectId = Form.useWatch("project", form)
  const blockCount = Form.useWatch("blockCount", form)
  const paymentsWatch = Form.useWatch("payments", form)
  const payments = Array.isArray(paymentsWatch) ? paymentsWatch : []
  const paymentModeKey = Form.useWatch(["payments", 0, "modeOfPayment"], form)
  const fractionalPaymentChannel =
    Form.useWatch("fractionalPaymentChannel", form) ?? FRACTIONAL_PAYMENT_CHANNEL_STANDARD
  const psidModeKeyResolved = useMemo(() => resolvePsidPaymentModeKey(modeOfPayments), [modeOfPayments])
  const isPsidPaymentFlow = useMemo(
    () => fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID || isPsidModeKey(paymentModeKey, modeOfPayments),
    [fractionalPaymentChannel, paymentModeKey, modeOfPayments]
  )

  const blocksTotal = useMemo(() => {
    if (!blockData) return 0
    return (+blockData?.pricePerBlock || 0) * +blockCount
  }, [blockData, blockCount])

  const totalTax = useMemo(() => {
    if (!blockCount) return 0
    return (filerStatus?.data?.filerTaxPercentage * blocksTotal) / 100
  }, [filerStatus, blocksTotal])

  const needsPsidProcessingFees = fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID
  const canFetchPsidProcessingFees =
    needsPsidProcessingFees &&
    clientId != null &&
    clientId !== "" &&
    projectId != null &&
    projectId !== "" &&
    blockCount != null &&
    +blockCount > 0

  const psidProcessingFeeTotal = useMemo(() => {
    if (!needsPsidProcessingFees || paymentApis.psidFeeData == null) return null
    const d = paymentApis.psidFeeData
    if (d.processingFeeTotal != null && +d.processingFeeTotal) {
      return +d.processingFeeTotal
    }
    const perBlock = +d.processingFeePerBlock || 0
    return perBlock * +blockCount
  }, [needsPsidProcessingFees, paymentApis.psidFeeData, blockCount])

  const grandTotal = useMemo(() => {
    if (!blockData) return 0
    return blocksTotal + (+totalTax || 0) + psidProcessingFeeTotal + (+filerStatus?.data?.partnerFee || 0)
  }, [blockData, blocksTotal, totalTax, psidProcessingFeeTotal, filerStatus?.data?.partnerFee])

  const psidProcessingFeesGateOk =
    !needsPsidProcessingFees ||
    (canFetchPsidProcessingFees &&
      paymentApis.psidFeesFetchSuccess &&
      !paymentApis.loadingPsidProcessingFees &&
      !paymentApis.psidFeesError)

  const selectedFractionalProject = useMemo(
    () => (projectOptions || []).find(o => String(o?.value) === String(projectId)),
    [projectOptions, projectId]
  )

  const paymentProofOcrEnabled = useMemo(() => {
    const nested = projectDetail?.Project
    return [
      projectDetail?.paymentProof,
      nested?.paymentProof,
      blockData?.paymentProof,
      selectedFractionalProject?.paymentProof,
    ].some(isPaymentProofVisionOnProject)
  }, [
    projectDetail?.paymentProof,
    projectDetail?.Project?.paymentProof,
    blockData?.paymentProof,
    selectedFractionalProject?.paymentProof,
  ])

  const isRaFlowEnabledForPayments = useMemo(() => {
    const flag = v => v === true || v === 1 || String(v) === "1" || String(v) === "true"
    return [
      projectDetail?.isRAFlowEnabled,
      projectDetail?.Project?.isRAFlowEnabled,
      blockData?.isRAFlowEnabled,
      selectedFractionalProject?.isRAFlowEnabled,
    ].some(flag)
  }, [
    projectDetail?.isRAFlowEnabled,
    projectDetail?.Project?.isRAFlowEnabled,
    blockData?.isRAFlowEnabled,
    selectedFractionalProject?.isRAFlowEnabled,
  ])

  const blockCountRules = useMemo(
    () => [
      { required: true, message: t("Please set block quantity") },
      {
        type: "number",
        min: 1,
        max: blockData?.availableBlockCount,
        message: t("Block count must be between 1 and {{max}}", { max: blockData?.availableBlockCount }),
      },
    ],
    [blockData?.availableBlockCount]
  )

  const onCancel = () => {
    psidSuccessModalRef.current?.setVisible(false)
    form.resetFields()
    form.setFieldsValue({
      blockCount: 1,
      fractionalPaymentChannel: FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
      payments: [{ enableDepositRA: false }],
    })
    formRef?.current?.setVisible(false)
  }

  const getFilerStatusAndProjects = () => {
    form.resetFields()
    form.setFieldsValue({
      project: undefined,
      blockCount: 1,
      fractionalPaymentChannel: FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
      payments: [{ enableDepositRA: false }],
    })
    getFilerStatus(client?.id || lead?.Client?.id)
    getFractionalProjects()
  }

  const onClientFormSuccess = () => {
    clientFormRef.current?.setVisible(false)
    getFilerStatusAndProjects()
  }
  const onClientFormCancel = () => {
    clientFormRef.current?.setVisible(false)
    onCancel()
  }

  const onModalOpen = useCallback(() => {
    console.log("++++++++++++++++++++lead", lead,"==",client)
    const clientId = lead?.Client?.id ?? client?.id
    if (clientId) {
      getClientDetail(lead?.Client?.id ?? client?.id)
        .unwrap()
        .then(res => {
          const missingFields = requiredFields?.filter(field => !res?.[field])
          if (!!missingFields?.length) {
            clientFormRef.current?.setVisible(true)
          } else {
            getFilerStatusAndProjects()
          }
        })
    }
  }, [form, getFractionalProjects, lead, client])

  useEffect(() => {
    if (projectId) {
      form.setFieldsValue({ blockCount: 1 })
      fetchBlockData(projectId, true)
      const pid = +projectId
      if (pid > 0) {
        getPaymentMethod({ id: pid, flag: false })
        fetchProjectDetail(pid)
      }
    }
  }, [projectId, fetchBlockData, form, getPaymentMethod, fetchProjectDetail])

  const debouncedFractionalPsidFees = useMemo(
    () =>
      debounce((inquiryId, projectIdNum, formInst, mutate) => {
        const blocks = +formInst.getFieldValue("blockCount")
        if (!(blocks > 0)) return
        mutate({ inquiryId, projectId: projectIdNum, numberOfBlocks: blocks })
          .unwrap()
          .catch(() => notification.error({ message: t("Could not load PSID processing fees") }))
      }, 450),
    []
  )

  useEffect(() => {
    if (!canFetchPsidProcessingFees || +projectId <= 0) return
    debouncedFractionalPsidFees(clientId, +projectId, form, paymentApis.fractionalProcessingFees)
  }, [
    canFetchPsidProcessingFees,
    clientId,
    projectId,
    blockCount,
    form,
    paymentApis.fractionalProcessingFees,
    debouncedFractionalPsidFees,
  ])

  const paymentTotalOk = useMemo(() => {
    const refRaw = +grandTotal || 0
    if (refRaw <= 0) return false
    const ref = Math.round(refRaw)
    const sum = Math.round(+(payments[0]?.amount || 0))
    return sum === ref
  }, [grandTotal, payments])

  const paymentsComplete = useMemo(() => {
    if (fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID) {
      return payments.length > 0 && isFractionalPsidChannelRowComplete(payments[0], psidModeKeyResolved)
    }
    return paymentsListFullyComplete(payments || [])
  }, [fractionalPaymentChannel, payments, psidModeKeyResolved])

  const onSubmit = async values => {
    const banks = paymentApis.banks ?? []
    const project = {
      id: +projectId,
      name: selectedFractionalProject?.label ?? projectDetail?.name ?? "",
    }
    const isPsid =
      values?.fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID ||
      isPsidModeKey(values?.payments?.[0]?.modeOfPayment, modeOfPayments)

    addSales({
      lead,
      leadSalesInfo: undefined,
      bookingForm: false,
      banks,
      modeOfPayments,
      stateManager: { dealPrice: grandTotal },
      optionalSelections: {},
      project,
      payments: values.payments,
      numberOfBlocks: values.blockCount,
      nextTask: -1,
      cdpCommitment: 100,
    })
      .unwrap()
      .then(res => {
        if (isPsid && res?.data?.psid) {
          formRef?.current?.setVisible(false)
          form.resetFields()
          form.setFieldsValue({
            blockCount: 1,
            fractionalPaymentChannel: FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
            payments: [{ enableDepositRA: false }],
          })
          psidSuccessModalRef.current?.setVisible(true, res?.data)
        } else {
          notification.success({ message: t("Sale submitted successfully") })
          formRef?.current?.setVisible(false)
          form.resetFields()
          form.setFieldsValue({
            blockCount: 1,
            fractionalPaymentChannel: FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
            payments: [{ enableDepositRA: false }],
          })
        }
      })
      .catch(e => {
        notification.error({ message: e?.error || e?.message || t("Could not submit sale") })
      })
  }

  const disableSubmit =
    !blockData ||
    loadingBlockData ||
    !projectId ||
    projectsError ||
    !modeOfPayments?.length ||
    loadingPaymentMethods ||
    !psidProcessingFeesGateOk ||
    !paymentTotalOk ||
    !paymentsComplete ||
    isAddingSale

  const renderBlocksCard = () => {
    const valuesLoading = loadingBlockData
    const valueSkeleton = <Skeleton active height={20} width={72} />
    const available = blockData?.availableBlockCount
    const sold = blockData?.soldFractions
    const availabilityTooltip =
      available == null
        ? null
        : sold != null && sold !== ""
          ? t("{{available}} blocks available. {{sold}} sold.", { available, sold })
          : t("{{count}} blocks available for this project.", { count: available })

    return (
      <Card
        Shadow={false}
        variant="gray"
        style={{
          borderRadius: 10,
        }}
        bodyStyle={{
          padding: 10,
          borderRadius: 10,
          backgroundColor: theme["gray100"],
        }}
        className={cx(styles.propertyBlock, "mbe-16")}
      >
        <Flex align="center" justify="space-between" gap={24}>
          <TitleDescription
            title={<Text type="secondary">{t("Price per block")}</Text>}
            description={valuesLoading ? valueSkeleton : formatAmount(blockData?.pricePerBlock)}
          />

          <Flex vertical align="center" gap={6} style={{ flexShrink: 0 }}>
            <Form.Item name="blockCount" rules={blockCountRules} style={{ margin: 0 }}>
              <BlockCountStepper
                count={blockCount}
                min={1}
                max={blockData?.availableBlockCount}
                disabled={valuesLoading}
                onChange={e => form.setFieldValue("blockCount", e)}
              />
            </Form.Item>
            {valuesLoading ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("Loading availability…")}
              </Text>
            ) : available != null ? (
              <Tooltip title={availabilityTooltip}>
                <Text type="secondary" style={{ fontSize: 12, cursor: "help" }}>
                  {t("of {{count}} available blocks", { count: available })}
                </Text>
              </Tooltip>
            ) : null}
          </Flex>
          <TitleDescription
            title={<Text type="secondary">{t("Total amount")}</Text>}
            description={valuesLoading ? valueSkeleton : formatAmount(blocksTotal)}
          />
        </Flex>
      </Card>
    )
  }

  const blockSummaryCardData = useMemo(() => {
    const processingFeesValue =
      fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID ? (
        paymentApis.loadingPsidProcessingFees ? (
          <Skeleton active height={20} width={72} />
        ) : (
          formatAmount(psidProcessingFeeTotal ?? 0)
        )
      ) : (
        formatAmount(blockData?.processingFee || 0)
      )

    return [
      {
        label: `${t("Property blocks")} (x${+blockCount > 0 ? blockCount : 1})`,
        value: formatAmount(blocksTotal),
      },
      {
        label: t("Advance tax ({{filerStatus}}) ({{filerTaxPercentage}}%)", {
          filerStatus: filerStatus?.data?.isFiler ? t("Filer") : t("Non Filer"),
          filerTaxPercentage: filerStatus?.data?.filerTaxPercentage,
        }),
        value: formatAmount(totalTax),
      },
      {
        label: t("Processing fees"),
        value: processingFeesValue,
      },
      {
        label: t("Partner fee"),
        value: formatAmount(filerStatus?.data?.partnerFee),
      },
    ]
  }, [
    blockCount,
    blocksTotal,
    filerStatus?.data?.isFiler,
    filerStatus?.data?.filerTaxPercentage,
    filerStatus?.data?.processingFee,
    filerStatus?.data?.partnerFee,
    fractionalPaymentChannel,
    blockData?.processingFee,
    paymentApis?.loadingPsidProcessingFees,
    psidProcessingFeeTotal,
    totalTax,
  ])

  const renderBlocksSummary = () => (
    <Card
      Shadow={false}
      className={styles.blocksSummaryCard}
      variant="gray"
      style={{ marginTop: 12, borderRadius: 8 }}
      title={<Text type="secondary">{t("Sale summary")}</Text>}
      headStyle={{
        background: theme["gray100"],
      }}
      bodyStyle={{ padding: "12px 16px" }}
      styles={{ "--card-padding-block": "0px" }}
    >
      {blockSummaryCardData.map(item => (
        <Flex justify="space-between" className={"mbe-4"}>
          <Text type="secondary">{item?.label}</Text>
          {item?.value}
        </Flex>
      ))}
      <Divider style={{ margin: "8px 0" }} />
      <Flex justify="space-between" className={"mbe-4"}>
        <Text type="secondary">{t("Grand total")}</Text>
        <span className={styles.grandTotalValue}>{formatAmount(grandTotal)}</span>
      </Flex>
    </Card>
  )

  return (
    <>
      <Modal
        ref={formRef}
        width={820}
        title={modalTitle(lead, client, filerStatus?.data?.isFiler)}
        okText={isPsidPaymentFlow ? t("Submit and Generate PSID") : t("Submit")}
        cancelText={t("Cancel")}
        onCancel={onCancel}
        onOk={() => form.submit()}
        onModalVisible={onModalOpen}
        disableOkButton={disableSubmit}
        loading={
          isAddingSale ||
          loadingProjects ||
          (!!projectId && (loadingBlockData || loadingPaymentMethods || loadingProjectDetail))
        }
        getContainer={false}
        destroyOnClose
        maskClosable={false}
      >
        {loadingFilerStatus || isFetching || loadingProjects ? (
          <FractionalSaleModalSkeleton />
        ) : (
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            initialValues={{
              blockCount: 1,
              fractionalPaymentChannel: FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
              payments: [{ enableDepositRA: false }],
            }}
            onFinish={onSubmit}
          >
            <Legend title={t("Project Details")} />
            <Form.Item
              name="project"
              label={t("Select Project")}
              rules={[{ required: true, message: t("Please select a project") }]}
              style={{ marginTop: 8 }}
            >
              <SelectInput
                optionFilterProp="label"
                loading={loadingProjects}
                options={projectOptions}
                placeholder={t("Select a fractional project")}
              />
            </Form.Item>

            {projectId && (blockData || loadingBlockData) && (
              <>
                {renderBlocksHeader()}
                {renderBlocksCard()}
              </>
            )}
            {blockData && projectId && !loadingBlockData && (
              <>
                {renderBlocksSummary()}
                <FractionalPaymentSection
                  form={form}
                  referenceAmount={grandTotal}
                  projectId={projectId}
                  modeOfPayments={modeOfPayments}
                  loadingPaymentMethods={loadingPaymentMethods}
                  paymentApis={paymentApis}
                  paymentOptions={{
                    isRaFlowEnabled: isRaFlowEnabledForPayments,
                    ocrEnabled: paymentProofOcrEnabled,
                  }}
                />
              </>
            )}
          </Form>
        )}
        {(projectsError || blockDataError) && (
          <Text type="danger" style={{ display: "block", marginTop: 8 }}>
            {t("Could not load fractional data. Showing cached test values if available.")}
          </Text>
        )}
      </Modal>
      <ClientForm
        formRef={clientFormRef}
        client={clientData}
        onSuccess={onClientFormSuccess}
        onCancel={onClientFormCancel}
        isFieldsRequired={true}
      />
      <PsidSuccessModal ref={psidSuccessModalRef} lead={lead} client={client} isFiler={filerStatus?.data?.isFiler} />
    </>
  )
}
